/**
 * Step Result Display Component
 *
 * Intelligently renders execution step output based on node type.
 * Supports different display modes for text, images, variables, etc.
 */

import { useState } from 'react';
import { useI18n } from '@/i18n';
import type { ExecutionStep } from '@/types/imageWorkflow';

interface StepResultDisplayProps {
  step: ExecutionStep;
}

export function StepResultDisplay({ step }: StepResultDisplayProps) {
  const { messages } = useI18n();
  const t = messages.imageWorkflows;
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { node_type, output_data, input_data, error_message, status } = step;

  // Handle error state
  if (status === 'failed' || error_message) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-1">
          <span>✗</span>
          {t?.stepResult?.error || 'Error'}
        </div>
        <div className="text-sm text-red-600">{error_message || 'Unknown error'}</div>
      </div>
    );
  }

  // Render based on node type
  switch (node_type) {
    case 'start':
      return renderStartOutput();
    case 'prompt':
      return renderPromptOutput();
    case 'generate':
    case 'edit':
    case 'fuse':
      return renderImageOutput();
    case 'output':
      return renderOutputNode();
    default:
      return renderGenericOutput();
  }

  function renderStartOutput() {
    const variables = output_data?.variables as Record<string, unknown> | undefined;
    const context = output_data?.context as { builtins?: Record<string, unknown>; variables?: Record<string, unknown> } | undefined;

    // Try to get variables from different possible locations
    const displayVars = variables || context?.builtins || context?.variables || {};

    return (
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-600 mb-1">
          {t?.stepResult?.variables || 'Variables'}
        </div>
        {Object.keys(displayVars).length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-2 space-y-1">
            {Object.entries(displayVars).map(([key, value]) => (
              <div key={key} className="flex items-start text-xs">
                <span className="font-mono text-gray-500 min-w-[80px]">{key}:</span>
                <span className="text-gray-700 break-all">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">
            {t?.stepResult?.noVariables || 'No variables'}
          </div>
        )}
      </div>
    );
  }

  function renderPromptOutput() {
    const mode = (output_data?.mode || input_data?.mode || input_data?.input_mode) as string | undefined;
    const agentType = (output_data?.agent_type || input_data?.agent_type) as string | undefined;
    const schemaSource = output_data?.schema_source as string | undefined;
    const extractedData = output_data?.extracted_data as Record<string, unknown> | undefined;

    // Collect output fields to display
    // Priority: extracted_data fields > individual output fields > prompt fallback
    const outputFields: Array<{ key: string; value: string; isSchema: boolean }> = [];

    // Internal fields to skip
    const internalFields = ['mode', 'agent_type', 'schema_source', 'extracted_data', 'tokens_used', 'model'];

    if (extractedData && typeof extractedData === 'object') {
      // Display extracted schema fields
      Object.entries(extractedData).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          outputFields.push({ key, value: JSON.stringify(value, null, 2), isSchema: true });
        } else if (value !== null && value !== undefined) {
          outputFields.push({ key, value: String(value), isSchema: true });
        }
      });
    } else if (output_data) {
      // Display individual output fields (schema fields are exposed at top level)
      Object.entries(output_data).forEach(([key, value]) => {
        if (internalFields.includes(key)) return;
        if (key === 'prompt' && outputFields.length > 0) return; // Skip prompt if we have other fields
        if (typeof value === 'object' && value !== null) {
          outputFields.push({ key, value: JSON.stringify(value, null, 2), isSchema: schemaSource !== undefined });
        } else if (value !== null && value !== undefined) {
          outputFields.push({ key, value: String(value), isSchema: schemaSource !== undefined });
        }
      });
    }

    // If no fields found, fallback to prompt
    if (outputFields.length === 0) {
      const prompt = (output_data?.prompt || output_data?.output_prompt) as string | undefined;
      if (prompt) {
        outputFields.push({ key: 'prompt', value: prompt, isSchema: false });
      }
    }

    const maxLength = 200;

    return (
      <div className="space-y-2">
        {/* Mode indicator */}
        {mode && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{t?.stepResult?.mode || 'Mode'}:</span>
            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-700 capitalize">
              {mode}
            </span>
            {agentType && (
              <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                {agentType}
              </span>
            )}
            {schemaSource && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                [schema]
              </span>
            )}
          </div>
        )}

        {/* Output fields */}
        <div className="text-xs font-medium text-gray-600">
          {t?.stepResult?.output || 'Output'}
        </div>
        {outputFields.length > 0 ? (
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            {outputFields.map(({ key, value, isSchema }) => {
              const isLong = value.length > maxLength;
              const displayText = isLong && !isExpanded ? value.slice(0, maxLength) + '...' : value;

              return (
                <div key={key}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-gray-600 capitalize">{key}:</span>
                    {isSchema && (
                      <span className="text-xs text-green-600">[schema]</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{displayText}</p>
                  {isLong && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="mt-1 text-xs text-primary-600 hover:text-primary-700"
                    >
                      {isExpanded
                        ? (t?.stepResult?.showLess || 'Show less')
                        : (t?.stepResult?.showMore || 'Show more')}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">
            {t?.stepResult?.noPrompt || 'No output generated'}
          </div>
        )}
      </div>
    );
  }

  function renderImageOutput() {
    const imageUrl = (output_data?.image_url || output_data?.temp_url) as string | undefined;
    const model = (input_data?.model || output_data?.model) as string | undefined;
    const taskId = output_data?.task_id as string | undefined;
    const width = output_data?.width as number | undefined;
    const height = output_data?.height as number | undefined;

    return (
      <div className="space-y-2">
        {/* Model info */}
        {model && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">{t?.stepResult?.model || 'Model'}:</span>
            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
              {model}
            </span>
          </div>
        )}

        {/* Image preview */}
        <div className="text-xs font-medium text-gray-600">
          {t?.stepResult?.output || 'Output'}
        </div>
        {imageUrl && !imageError ? (
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={imageUrl}
              alt="Generated image"
              className="w-full h-auto max-h-64 object-contain"
              onError={() => setImageError(true)}
              loading="lazy"
            />
            {/* Dimensions overlay */}
            {(width || height) && (
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded">
                {width}×{height}
              </div>
            )}
          </div>
        ) : imageError ? (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <span className="text-2xl">🖼️</span>
            <p className="text-xs text-gray-500 mt-1">
              {t?.stepResult?.imageLoadError || 'Failed to load image'}
            </p>
          </div>
        ) : (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <span className="text-2xl">🖼️</span>
            <p className="text-xs text-gray-500 mt-1">
              {t?.stepResult?.noImage || 'No image generated'}
            </p>
          </div>
        )}

        {/* Task ID reference */}
        {taskId && (
          <div className="text-xs text-gray-400">
            Task: {taskId.slice(0, 8)}...
          </div>
        )}
      </div>
    );
  }

  function renderOutputNode() {
    const imageUrl = (output_data?.image_url || output_data?.permanent_url) as string | undefined;
    const saved = output_data?.saved as boolean | undefined;
    const category = output_data?.category as string | undefined;
    const filename = output_data?.filename as string | undefined;

    return (
      <div className="space-y-2">
        {/* Storage status */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">{t?.stepResult?.storage || 'Storage'}:</span>
          {saved ? (
            <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
              <span>✓</span> {t?.stepResult?.saved || 'Saved to Library'}
            </span>
          ) : (
            <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
              {t?.stepResult?.temporary || 'Temporary'}
            </span>
          )}
        </div>

        {/* Category and filename */}
        {(category || filename) && (
          <div className="text-xs text-gray-500">
            {category && <span>{category}</span>}
            {filename && <span> / {filename}</span>}
          </div>
        )}

        {/* Image preview */}
        {imageUrl && !imageError ? (
          <div className="relative bg-gray-100 rounded-lg overflow-hidden">
            <img
              src={imageUrl}
              alt="Output image"
              className="w-full h-auto max-h-64 object-contain"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          </div>
        ) : (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <span className="text-2xl">📤</span>
            <p className="text-xs text-gray-500 mt-1">
              {t?.stepResult?.outputPending || 'Output pending'}
            </p>
          </div>
        )}
      </div>
    );
  }

  function renderGenericOutput() {
    const hasOutput = output_data && Object.keys(output_data).length > 0;

    return (
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-600">
          {t?.stepResult?.output || 'Output'}
        </div>
        {hasOutput ? (
          <pre className="bg-gray-50 rounded-lg p-3 text-xs text-gray-700 overflow-x-auto">
            {JSON.stringify(output_data, null, 2)}
          </pre>
        ) : (
          <div className="text-xs text-gray-400 italic">
            {t?.stepResult?.noOutput || 'No output data'}
          </div>
        )}
      </div>
    );
  }
}

export default StepResultDisplay;
